import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Broadcast, ArrowLeft, Play, Users } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

export default function Live() {
  const nav = useNavigate();
  const [active, setActive] = useState([]);
  const [replays, setReplays] = useState([]);
  const [popular, setPopular] = useState([]);
  const [streamEnabled, setStreamEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [cfg, act, rep, pop] = await Promise.all([
          api.get("/live/config"),
          api.get("/live/active"),
          api.get("/live/replays"),
          api.get("/live/popular?limit=10"),
        ]);
        setStreamEnabled(!!cfg.data.stream_enabled);
        setActive(act.data || []);
        setReplays(rep.data || []);
        setPopular(pop.data?.items || []);
      } catch (e) {
        // best-effort
      } finally {
        setLoading(false);
      }
    })();
    const interval = setInterval(async () => {
      try {
        const [{ data: act }, { data: pop }] = await Promise.all([
          api.get("/live/active"),
          api.get("/live/popular?limit=10"),
        ]);
        setActive(act || []);
        setPopular(pop?.items || []);
      } catch (e) { /* ignore */ }
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const leagueStyle = (label) => {
    const L = (label || "D")[0];
    if (L === "A") return "bg-jam-gold/20 text-jam-gold";
    if (L === "B") return "bg-violet-500/20 text-violet-300";
    if (L === "C") return "bg-papaya/20 text-papaya";
    return "bg-white/10 text-cream/70";
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="live-page">
      <div className="px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="live-back">
          <ArrowLeft size={18} />
        </Link>
        <button
          onClick={() => nav("/live/go")}
          disabled={!streamEnabled}
          data-testid="live-go-live-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-xs hover:bg-papaya-hover disabled:opacity-40 transition-colors papaya-glow"
        >
          <Broadcast size={16} weight="fill" /> Go Live
        </button>
      </div>

      <div className="px-5 mt-5">
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Live Now</p>
        <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">The diaspora is on stage</h1>
        <p className="text-cream/60 text-sm mt-2">Real-time soca, dancehall, carnival countdowns. Jump in.</p>
      </div>

      {loading ? (
        <div className="px-5 mt-10 text-cream/50 text-xs tracking-[0.3em] uppercase animate-pulse">Tuning in…</div>
      ) : (
        <>
          {popular.length > 0 && (
            <div className="mt-6 px-5" data-testid="live-popular">
              <div className="flex items-center justify-between mb-3">
                <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">🔥 Hourly Popular</p>
                <span className="text-cream/40 text-[10px] tracking-[0.2em] uppercase">Top 10</span>
              </div>
              <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
                {popular.map((p) => (
                  <button
                    key={p.session_id}
                    onClick={() => nav(`/live/watch/${p.session_id}`)}
                    data-testid={`popular-live-${p.rank}`}
                    className="shrink-0 w-40 text-left rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-papaya transition-colors active:scale-[0.98]"
                  >
                    <div className="aspect-[4/5] bg-gradient-to-br from-papaya/20 via-ink to-ink relative">
                      {p.host_avatar_path ? (
                        <img src={fileUrl(p.host_avatar_path)} alt="" className="w-full h-full object-cover opacity-70" />
                      ) : null}
                      <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-papaya text-ink text-xs font-black flex items-center justify-center">{p.rank}</div>
                      <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-black tracking-widest uppercase ${leagueStyle(p.league_label)}`}>{p.league_label}</div>
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-cream font-black text-xs leading-tight line-clamp-2">{p.title}</p>
                        <p className="text-cream/70 text-[10px] mt-1 flex items-center gap-1"><Users size={10} weight="fill" /> {(p.viewers || 0).toLocaleString()} · @{p.host_username}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 px-5">
            {active.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-white/15 p-6 text-center" data-testid="live-empty-active">
                <div className="text-3xl mb-2">📡</div>
                <p className="text-cream font-bold">Nobody live yet.</p>
                <p className="text-cream/50 text-xs mt-1">Be the first — tap Go Live up top.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {active.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => nav(`/live/watch/${s.session_id}`)}
                    data-testid={`live-card-${s.session_id}`}
                    className="text-left rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-papaya transition-colors active:scale-[0.98]"
                  >
                    <div className="aspect-[3/4] bg-gradient-to-br from-papaya/20 via-ink to-ink relative">
                      {s.host_avatar_path ? (
                        <img src={fileUrl(s.host_avatar_path)} alt="" className="w-full h-full object-cover opacity-70" />
                      ) : null}
                      <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                      <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-red-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-cream animate-pulse" />
                        <span className="text-cream text-[10px] tracking-[0.25em] uppercase font-black">Live</span>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-cream font-black text-sm leading-tight line-clamp-2">{s.title}</p>
                        <p className="text-cream/70 text-[11px] mt-1">@{s.host_username}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {replays.length > 0 && (
            <div className="mt-10 px-5">
              <p className="text-cream/60 text-[10px] tracking-[0.3em] uppercase font-bold mb-3">Recent Replays</p>
              <div className="space-y-2">
                {replays.slice(0, 12).map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => nav(`/live/replay/${s.session_id}`)}
                    data-testid={`replay-card-${s.session_id}`}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-papaya transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl bg-papaya/20 flex items-center justify-center flex-shrink-0">
                      <Play size={20} weight="fill" className="text-papaya" />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-cream font-bold truncate text-sm">{s.title}</p>
                      <p className="text-cream/60 text-xs">@{s.host_username}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
